import React from 'react'
import { css, type SerializedStyles } from '@emotion/react'
import { colors } from '@fs/zion-ui'

import { getBackgroundVerticalAlignItems } from '../../utils/getAlignItems'
import useInView from '../../hooks/useInView'
import useResponsiveImage from '../../hooks/useResponsiveImage'
import useResponsiveValues from '../../hooks/useResponsiveValues'

type SectionBackgroundProps = ZionCMSSection['background'] & {
  children?: React.ReactNode
}

export default function SectionBackground({
  bottomBorderImage,
  color,
  gradient,
  gradientOverlay,
  image,
  topBorderImage,
  children,
}: SectionBackgroundProps) {
  const [ref, isInView] = useInView<HTMLDivElement>()
  const { isTiled, verticalCropPreference } = useResponsiveValues(image?.layout)
  const { isTiled: topBorderImageIsTiled } = useResponsiveValues(topBorderImage?.layout)
  const { isTiled: bottomBorderImageIsTiled } = useResponsiveValues(bottomBorderImage?.layout)
  const bgImageUrl = useResponsiveImage(image?.url, image?.sizes)
  const topBorderImageUrl = useResponsiveImage(topBorderImage?.url, topBorderImage?.sizes)
  const bottomBorderImageUrl = useResponsiveImage(bottomBorderImage?.url, bottomBorderImage?.sizes)

  const bgImageInView = (bgUrl): string => {
    return isInView ? bgUrl : ''
  }

  return (
    <div
      css={backgroundCss({
        bottomBorderImageIsTiled,
        bottomBorderImageUrl: bgImageInView(bottomBorderImageUrl),
        color: colors[color || ''],
        gradient,
        gradientOverlay,
        imageUrl: bgImageInView(bgImageUrl),
        isTiled,
        topBorderImageIsTiled,
        topBorderImageUrl: bgImageInView(topBorderImageUrl),
        verticalCropPreference,
      })}
      ref={ref}
    >
      {children}
    </div>
  )
}

/**
 * CSS helper functions
 */

type BackgroundCssProps = {
  bottomBorderImageIsTiled?: boolean
  bottomBorderImageUrl?: string
  color?: string
  gradient?: ZionCMSGradient
  gradientOverlay?: SectionBackgroundProps['gradientOverlay']
  imageUrl?: string
  isTiled?: boolean
  topBorderImageIsTiled?: boolean
  topBorderImageUrl?: string
  verticalCropPreference?: VerticalAlignment
}

/**
 * Function that returns the background CSS for a section.
 */
const backgroundCss = ({
  bottomBorderImageIsTiled,
  bottomBorderImageUrl,
  color,
  gradient,
  gradientOverlay,
  imageUrl,
  isTiled,
  topBorderImageIsTiled,
  topBorderImageUrl,
  verticalCropPreference,
}: BackgroundCssProps): SerializedStyles => {
  const backgroundCssString = [] as string[]

  backgroundCssString.push(getImageCss(topBorderImageUrl, 'top', topBorderImageIsTiled, true))
  backgroundCssString.push(getImageCss(bottomBorderImageUrl, 'bottom', bottomBorderImageIsTiled, true))

  backgroundCssString.push(gradientOverlay?.gradientCss || '')

  const imageAlignment = getBackgroundVerticalAlignItems(verticalCropPreference) || 'center'
  backgroundCssString.push(getImageCss(imageUrl, imageAlignment, isTiled))

  backgroundCssString.push(getGradientCss(gradient))

  backgroundCssString.push(color || '')

  return css`
    position: relative;
    background: ${backgroundCssString.filter((c) => c).join(', ')};
  `
}

function getImageCss(imageUrl, alignment, isTiled, borderImage = false) {
  if (!imageUrl) {
    return ''
  }
  let imageRepeat = isTiled ? 'repeat' : 'no-repeat'
  if (isTiled && borderImage) {
    imageRepeat = 'repeat-x'
  }
  const imageSize = isTiled ? 'auto' : 'cover'
  return `${alignment} / ${imageSize} ${imageRepeat} url(${imageUrl})`
}

function getGradientCss(gradient?: ZionCMSGradient) {
  if (!gradient || !gradient.topColor || !gradient.bottomColor) {
    return ''
  }
  return `
    linear-gradient(
      180deg,
      ${colors[gradient.topColor]} 0%,
      ${colors[gradient.bottomColor]} 100%
    )
  `
}
